import React from "react";
import {
  Autocomplete,
  TextField,
  FormHelperText,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { InputField } from "./Inputs";
import InputLabelComp from "./InputLabelComp";
import { useDispatch, useSelector } from "react-redux";
import {
  setNotInList,
  removeNotInList,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import { RootState } from "../../services/redux/store";

interface Option {
  value: string | number;
  label: string | number | React.ReactNode;
}

interface AutoCompleteOptionsProps {
  options?: Option[];
  label?: string;
  disabled?: boolean;
  value?: Option | null;
  isMandatory?: boolean;
  onChange: (value: Option | null) => void;
  helperText?: string;
  error?: boolean;
  loading?: boolean;
  placeholder?: string;
  onInputChange?: any;
  inputValue?: string;
  fieldProps?: any;
  form?: any;
  isNotList?: boolean;
  formikRef?: any;
  noOptionText?: string;
}

const AutoCompleteOptions: React.FC<AutoCompleteOptionsProps> = ({
  options = [],
  label,
  disabled = false,
  value = null,
  isMandatory = false,
  onChange,
  helperText,
  error = false,
  loading = false,
  placeholder = "Select...",
  inputValue = "",
  onInputChange,
  fieldProps,
  form,
  isNotList = false,
  noOptionText = "No options",
}) => {
  const notInList = useSelector(
    (state: RootState) => state?.stepper?.notInList
  );

  const [localInputValue, setLocalInputValue] = React.useState("");
  const debounceTimeoutRef = React.useRef<NodeJS.Timeout>();
  const formValuesRef = React.useRef<any>();

  const dispatch = useDispatch();

  const labelId = `autocomplete-label-${label
    ?.replace(/\s+/g, "-")
    .toLowerCase()}`;

  // Store form values in ref to avoid unnecessary re-renders
  React.useEffect(() => {
    if (form?.values) {
      formValuesRef.current = form.values;
    }
  });

  const handleNotInListClick = React.useCallback(
    (value: string, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const fieldName = String(fieldProps?.name || "");
      const enableNotInList = !notInList?.includes(value);
      const dependentFieldLabelsByName: Record<string, string[]> = {
        university: ["Institute Name", "Course Name"],
        institute: ["Course Name"],
        courseName: [],
      };
      const dependentFieldNamesByName: Record<string, string[]> = {
        university: [
          "institute",
          "course",
          "courseName",
          "universityCampusId",
          "institute_code",
          "courseCode",
        ],
        institute: ["course", "courseName", "institute_code", "courseCode"],
        courseName: ["course", "courseCode"],
      };

      form.setFieldValue(fieldName, "");
      form.setFieldError(fieldName, undefined);
      setLocalInputValue("");

      dependentFieldNamesByName[fieldName]?.forEach((dependentFieldName) => {
        form.setFieldValue(dependentFieldName, "");
        form.setFieldError(dependentFieldName, undefined);
      });

      const labelsToToggle = [value, ...(dependentFieldLabelsByName[fieldName] || [])];

      labelsToToggle.forEach((label) => {
        if (enableNotInList) {
          if (!notInList?.includes(label)) {
            dispatch(setNotInList(label));
          }
          return;
        }

        if (notInList?.includes(label)) {
          dispatch(removeNotInList(label));
        }
      });

      if (fieldName === "university") {
        form.setFieldValue("institute", "");
        form.setFieldValue("course", "");
        form.setFieldValue("courseName", "");
      } else if (fieldName === "institute") {
        form.setFieldValue("course", "");
        form.setFieldValue("courseName", "");
      }
    },
    [form, fieldProps?.name, notInList, dispatch]
  );

  // Debounced function to update form value
  const debouncedFormUpdate = React.useCallback(
    (value: string) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        if (form && fieldProps?.name) {
          form.setFieldValue(fieldProps.name, value);
        }
      }, 300); // 300ms delay
    },
    [form, fieldProps?.name]
  );

  // Handle input change with local state and debounced form update
  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalInputValue(newValue);
      debouncedFormUpdate(newValue);
    },
    [debouncedFormUpdate]
  );

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Initialize local input value when field value changes - only when necessary
  React.useEffect(() => {
    if (form && fieldProps?.name) {
      const fieldValue = form.values[fieldProps.name];
      if (
        fieldValue &&
        typeof fieldValue === "string" &&
        fieldValue !== localInputValue
      ) {
        setLocalInputValue(fieldValue);
      }
    }
  }, [fieldProps?.name]); // Removed form?.values dependency to prevent unnecessary re-renders

  return (
    <div className="form-container">
      {/* Label */}
      <Box sx={{ mb: 0.5, lineHeight: 1 }}>
        {isNotList ? (
          <label
            id={labelId}
            className="text-lable"
            htmlFor="autocomplete-input"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>
              {label} {isMandatory && <span style={{ color: "red" }}>*</span>}
            </span>
            <Typography
              sx={{
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "500",
                color: "rgba(0, 179, 181, 1)",
                "&:hover": {
                  color: "rgba(0, 150, 152, 1)",
                },
              }}
              onClick={(event) => handleNotInListClick(label || "", event)}
            >
              {notInList?.includes(label || "") ? "In list" : "Not in list"}
            </Typography>
          </label>
        ) : (
          <InputLabelComp
            title={label || ""}
            css={false}
            isMandatory={isMandatory}
          />
        )}
      </Box>

      {isNotList && notInList?.includes(label || "") ? (
        <InputField
          {...fieldProps}
          fullWidth
          placeholder={`Enter ${label?.toLowerCase() || "value"}`}
          variant="outlined"
          error={false}
          helperText={""}
          value={localInputValue}
          onChange={handleInputChange}
          disabled={disabled}
        />
      ) : (
        <Autocomplete
          onInputChange={(event, newInputValue) => {
            onInputChange?.(event, newInputValue);
          }}
          id="autocomplete-input"
          value={value}
          options={options}
          disabled={disabled}
          onChange={(_, newValue) => onChange(newValue)}
          getOptionLabel={(option) => {
            if (!option) return "";
            if (typeof option === "string") return option;
            return option.label !== undefined ? String(option.label) : "";
          }}
          isOptionEqualToValue={(option, val) => option.value === val.value}
          loading={loading}
          noOptionsText={noOptionText}
          popupIcon={loading ? <CircularProgress size={16} /> : undefined}
          fullWidth
          size="small"
          sx={{
            textTransform: "capitalize !important",
            "& .MuiOutlinedInput-root": {
              borderRadius: "6px",
              height: "38px",
              paddingRight: "8px !important",
              backgroundColor: "#FFFFFF",
            },
            "& .MuiAutocomplete-input": {
              padding: "0px 6px !important",
              fontSize: "11px",
              color: "#a5a3ae",
              fontWeight: "400",
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              error={error}
              placeholder={placeholder}
              variant="outlined"
            />
          )}
        />
      )}

      {/* Helper Text */}
      {helperText && (
        <FormHelperText
          error={error}
          style={{ marginTop: "4px", fontSize: "11px" }}
        >
          {helperText}
        </FormHelperText>
      )}
    </div>
  );
};

export default AutoCompleteOptions;
